/**
 * EVM chain display helpers. Mirrors deepnets_api/src/utils/chains.ts.
 * Solana tokens carry no `chain`; EVM tokens are 0x addresses plus a chain id,
 * passed to the API as `?chain=` (GET) or `chain` (POST body).
 */
export type EvmChainId = 'robinhood' | 'base' | 'ethereum' | 'bsc' | 'arc';

export const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

export const isEvmAddress = (s: string | null | undefined): boolean => !!s && EVM_ADDRESS_RE.test(s.trim());

interface ChainDisplay {
  name: string;
  short: string;
  explorer: string;
  /** GMGN kline chain slug, when GMGN charts this chain. */
  gmgn?: string;
  dexscreener: string;
  badgeClass: string;
}

export const EVM_CHAIN_IDS: EvmChainId[] = ['robinhood', 'base', 'ethereum', 'bsc', 'arc'];

export const EVM_CHAIN_INFO: Record<EvmChainId, ChainDisplay> = {
  robinhood: { name: 'Robinhood Chain', short: 'RH', explorer: 'https://robinhoodchain.blockscout.com', dexscreener: 'robinhood', badgeClass: 'text-lime-300 bg-lime-500/10 border-lime-500/30' },
  base: { name: 'Base', short: 'BASE', explorer: 'https://basescan.org', gmgn: 'base', dexscreener: 'base', badgeClass: 'text-blue-300 bg-blue-500/10 border-blue-500/30' },
  ethereum: { name: 'Ethereum', short: 'ETH', explorer: 'https://etherscan.io', gmgn: 'eth', dexscreener: 'ethereum', badgeClass: 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30' },
  bsc: { name: 'BNB Chain', short: 'BSC', explorer: 'https://bscscan.com', gmgn: 'bsc', dexscreener: 'bsc', badgeClass: 'text-yellow-300 bg-yellow-500/10 border-yellow-500/30' },
  arc: { name: 'Arc', short: 'ARC', explorer: 'https://explorer.arc.io', dexscreener: 'arc', badgeClass: 'text-cyan-300 bg-cyan-500/10 border-cyan-500/30' },
};

/** Canonical token page path: /token/<mint> on Solana, /token/<chain>/<0x…> on EVM chains. */
export function tokenPath(mint: string, chain?: string | null): string {
  const c = parseEvmChain(chain);
  return c ? `/token/${c}/${mint.toLowerCase()}` : `/token/${mint}`;
}

/** Wallet page path: /wallet/<addr> on Solana, /wallet/<chain>/<0x…> on EVM chains. */
export function walletPath(address: string, chain?: string | null): string {
  const c = parseEvmChain(chain);
  return c ? `/wallet/${c}/${address.toLowerCase()}` : `/wallet/${address}`;
}

/** Network page path. EVM has no network pages yet; the root wallet's page lists its funded wallets. */
export function networkPath(address: string, chain?: string | null): string {
  return parseEvmChain(chain) ? walletPath(address, chain) : `/network/${address}`;
}

/** Explorer URL for an address / tx / token, Solscan on Solana. */
export function explorerUrl(kind: 'address' | 'tx' | 'token', value: string, chain?: string | null): string {
  const c = parseEvmChain(chain);
  if (c) return `${EVM_CHAIN_INFO[c].explorer}/${kind}/${value}`;
  return `https://solscan.io/${kind === 'address' ? 'account' : kind}/${value}`;
}

/** Holder-analysis page path, chain-scoped for EVM tokens. */
export function holderAnalysisPath(mint: string, chain?: string | null, analysis = 'latest'): string {
  const c = parseEvmChain(chain);
  return c ? `/holder-analysis/${c}/${mint.toLowerCase()}/${analysis}` : `/holder-analysis/${mint}/${analysis}`;
}

export function parseEvmChain(v: string | null | undefined): EvmChainId | null {
  const k = (v || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(EVM_CHAIN_INFO, k) ? (k as EvmChainId) : null;
}

/** Chart iframe URL for an EVM token: GMGN where supported, DexScreener's embed otherwise. */
export function evmChartUrl(chain: EvmChainId, address: string, interval: string): string {
  const c = EVM_CHAIN_INFO[chain];
  if (c.gmgn) return `https://www.gmgn.cc/kline/${c.gmgn}/${address}?theme=dark&interval=${interval}&ref=9E9bbGF8`;
  return `https://dexscreener.com/${c.dexscreener}/${address}?embed=1&loadChartSettings=0&trades=0&tabs=0&info=0&chartLeftToolbar=0&chartTheme=dark&theme=dark&chartStyle=1&chartType=usd&interval=15`;
}

export interface ChainCandidate {
  chain: EvmChainId;
  chainName: string;
  symbol: string | null;
  name: string | null;
  liquidityUsd: number;
  pairs: number;
  icon?: string | null;
}
